import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import type { CroppedImage } from "./StepSplit";

interface StepSelectProps {
  images: CroppedImage[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
}

/** Inline SVG matching Figma ic_checkbox_on (16x16, #242946 fill, white stroke) */
function CheckOnIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M12.0005 0.833328C13.7492 0.833504 15.1665 2.25153 15.1665 4.00032V12.0003C15.1663 13.749 13.7491 15.1662 12.0005 15.1663H4.00049C2.2517 15.1663 0.833672 13.7491 0.833496 12.0003V4.00032C0.833496 2.25142 2.25159 0.833328 4.00049 0.833328H12.0005ZM11.1079 5.88216C11.0428 5.81726 10.9376 5.81712 10.8726 5.88216L7.57275 9.18196C7.37759 9.37708 7.061 9.37688 6.86572 9.18196L5.45166 7.7679C5.38658 7.70285 5.28041 7.70285 5.21533 7.7679C5.15075 7.83283 5.15052 7.93815 5.21533 8.00325L7.10107 9.88899C7.13232 9.92024 7.17506 9.93781 7.21924 9.93782C7.26283 9.93773 7.30499 9.92031 7.33643 9.88899L11.1079 6.11751C11.173 6.05242 11.173 5.94724 11.1079 5.88216Z"
        fill="#242946"
        stroke="white"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function StepSelect({ images, selectedIds, onSelectionChange }: StepSelectProps) {
  const [objectUrls, setObjectUrls] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    const urls = new Map<string, string>();
    for (const img of images) {
      urls.set(img.id, URL.createObjectURL(img.blob));
    }
    setObjectUrls(urls);
    return () => {
      for (const url of urls.values()) {
        URL.revokeObjectURL(url);
      }
    };
  }, [images]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const handleToggle = (id: string) => {
    if (selectedSet.has(id)) {
      onSelectionChange(selectedIds.filter((sid) => sid !== id));
    } else {
      onSelectionChange([...selectedIds, id]);
    }
  };

  return (
    <Container>
      <Grid>
        {images.map((img) => {
          const selected = selectedSet.has(img.id);
          return (
            <ImageCard
              key={img.id}
              $selected={selected}
              onClick={() => handleToggle(img.id)}
            >
              <CardImage src={objectUrls.get(img.id)} alt="split" />
              {selected && <DimOverlay />}
              {selected && (
                <CheckboxWrapper>
                  <CheckOnIcon />
                </CheckboxWrapper>
              )}
            </ImageCard>
          );
        })}
      </Grid>
    </Container>
  );
}

const Container = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
`;

const ImageCard = styled.div<{ $selected: boolean }>`
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  border: ${({ $selected }) =>
    $selected ? "2px solid #242946" : "1px solid rgba(143, 149, 178, 0.32)"};
  cursor: pointer;
  aspect-ratio: 16 / 9;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const DimOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.24);
  pointer-events: none;
`;

const CheckboxWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  padding: 6px;
  display: flex;
  line-height: 0;
`;
